// ./src/pages/private/EventSchedulePage/SlotDrawer.tsx
//
// Add / edit a single time slot. Two modes:
//   • agenda / single (showAgenda) → RELATIVE: pick a Day (of the event) + start/
//     end TIME only (no date). Overnight is automatic (end ≤ start → next day).
//     Stored as dayOffset + start/end time; the server materializes absolute
//     times from the event start, so moving the event date auto-shifts them.
//   • recurring custom date → ABSOLUTE: a full date+time picker.
// Saves immediately via EventTimeSlotService; uses the shared AppDrawer (pinned
// Save/Cancel).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack, Group, Text, Divider, ActionIcon, Tooltip, Button } from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTranslation } from 'react-i18next';

import { OpResult } from '@sdflc/api-helpers';

import { EventTimeSlotService, EventTimeSlot, EventTimeSlotInput, Event, Venue, VENUE_KIND, PolicyContentFormat, WHOLE_SET } from '@/utils/api';
import { VenuesProvider } from '@/contexts';
import { useToast, useModalShortcuts, useFormSnapshot, useDeadlineFollowsEnd, SHORTCUT_HINTS } from '@/hooks';
import {
  AppDrawer,
  DiscardGuard,
  AppIcon,
  TextField,
  TextAreaField,
  NumberField,
  SelectField,
  DateTimeField,
  SwitchField,
  isPastDateTime,
  formatMarkedDays,
  formatPickerMoment,
} from '@/components/generic';
import {
  deadlineCeiling,
  DEADLINE_END_OF_DAY,
  utcToZonedString,
  zonedStringToUtc,
  moveEndWithStart,
  latestEnd,
  shiftEndTimeKeepingDuration,
  addMinutesToDateTime,
  isEndBeforeStart,
  normalizePickerValue, getFormErrors, formatErrors } from '@/utils';
import { doorsOpenOptions, formatDoorsOpenLead, resolveDoorsOpenMinutes } from '@/utils/eventDates';
import { EventDurationBadge, eventZoneNote } from '@/components/features/Events';
import { VenuePickerField } from '@/components/features/CreateEventWizard/components/VenuePickerField';
import { MapElementSelect } from '@/components/features/EventMap';
import { PolicyTextEditor } from '@/components/features/Policies/PolicyTextEditor/PolicyTextEditor';

import { SlotSpeakersField } from './SlotSpeakersField';
import { SlotActsSummary } from './SlotActsSummary';
import { carryDescriptionOver } from './slotSpeakerRules';
import { canCancelDate } from './dateCancelStage';

dayjs.extend(utc);
dayjs.extend(timezone);

// ⚠ A date's pickers are wall clocks in the EVENT's time zone (2026-10-04) — what the site, the door and the time rules
//   read; the relative (agenda) mode always was. `tz` is the event's zone.
const toPicker = (iso: string | null | undefined, tz: string, dateOnly = false): string | null => utcToZonedString(iso, tz, dateOnly);

// "HH:mm" minute math for keep-duration on relative times.
const timeToMin = (t: string): number => {
  const [h, m] = t.split(':').map((n) => Number(n));
  return h * 60 + m;
};
const minToTime = (min: number): string => {
  const v = ((min % 1440) + 1440) % 1440;
  return `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
};


// Server error names that map to a visible input: known → inline under the field,
// anything else → generic, in the alert above Save.
// A session's description has no merge variables — the menu must offer none (its renderer would print them blank).
const NO_VARIABLES: never[] = [];
const RICHTEXT: PolicyContentFormat = 'richtext';
const PLAIN: PolicyContentFormat = 'plain';

const KNOWN_FORM_FIELDS = ['name', 'description', 'descriptionFormat', 'speakerGuestIds', 'track', 'rsvpEnabled', 'capacity', 'venueId', 'mapElementId', 'onlineMeetingUrl', 'rsvpDeadline', 'doorsOpenMinutes', 'canceled', 'cancelReason', 'startsAt', 'endsAt', 'isAllDay', 'dayOffset', 'startTime', 'endTime'];

interface SlotForm {
  name: string;
  description: string;
  /** POLICY_CONTENT_FORMAT — a NEW session starts as rich text; an existing one keeps the format it was written in. */
  descriptionFormat: string;
  /** The team members shown on the session, in order. */
  speakerGuestIds: string[];
  track: string;
  rsvpEnabled: boolean;
  capacity: number | null;
  // where
  venueId: string | null;
  mapElementId: string | null;
  onlineMeetingUrl: string;
  // rsvp
  rsvpDeadline: string | null; // local "YYYY-MM-DD HH:mm:ss" for the picker
  /** #41 — this date's own door as a picker STRING; '' = inherit the event's. '0' ≠ ''. */
  doorsOpenMinutes: string;
  // cancellation (edit-only)
  canceled: boolean;
  cancelReason: string;
  // absolute (recurring custom)
  startsAt: string | null;
  endsAt: string | null;
  isAllDay: boolean;
  // relative (agenda / single)
  dayOffset: number;
  startTime: string;
  endTime: string;
}

const emptyForm = (): SlotForm => ({
  name: '',
  description: '',
  descriptionFormat: RICHTEXT,
  speakerGuestIds: [],
  track: '',
  rsvpEnabled: false,
  capacity: null,
  venueId: null,
  mapElementId: null,
  onlineMeetingUrl: '',
  rsvpDeadline: null,
  doorsOpenMinutes: '',
  canceled: false,
  cancelReason: '',
  startsAt: null,
  endsAt: null,
  isAllDay: false,
  dayOffset: 0,
  startTime: '',
  endTime: '',
});

export interface SlotDrawerProps {
  opened: boolean;
  onClose: () => void;
  event: Event;
  slot: EventTimeSlot | null;
  /** Relative (day + time) mode — for agenda + single events. */
  showAgenda: boolean;
  /** The saved session's id — the page highlights its row (the confirmation; there is no success toast). */
  onSaved: (slotId: string | null) => void;
  /**
   * The event's other slots. A NEW session starts where the last one ENDS, for as long as that one ran (owner,
   * 2026-09-30: "when user adds several time slots … auto-calculate next start time as end of previous").
   */
  siblings?: EventTimeSlot[];
  /**
   * #199 — open the "Cancel this date" stage for this date (also its refund progress once cancelled). Cancelling is not a
   * field of this form: it tells everyone booked on the date and can refund them.
   */
  onCancelDate?: (slot: EventTimeSlot) => void;
}

export const SlotDrawer: React.FC<SlotDrawerProps> = ({ opened, onClose, event, slot, showAgenda, onSaved, siblings = [], onCancelDate }) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const [form, setForm] = useState<SlotForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  // The description editor is in full screen — the drawer's own Esc / click-outside are off meanwhile (see below).
  const [descriptionFullscreen, setDescriptionFullscreen] = useState(false);
  const [submitError, setSubmitError] = useState<OpResult | null>(null);

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(submitError, KNOWN_FORM_FIELDS),
    [submitError],
  );
  const nameInputRef = useRef<HTMLInputElement>(null);

  const relative = showAgenda;
  const eventId = event.id as string;
  const tz = event.timezone || dayjs.tz.guess();
  // The pickers below are in the event's zone — said when this device keeps another one.
  const zoneNote = eventZoneNote(t, tz, 'elsewhere');

  // How many days the event spans (1 for a single-day event → no Day picker).
  const dayCount = useMemo(() => {
    const start = dayjs.utc(event.startsAt).tz(tz);
    const end = event.endsAt ? dayjs.utc(event.endsAt).tz(tz) : start;
    return Math.max(1, end.startOf('day').diff(start.startOf('day'), 'day') + 1);
  }, [event.startsAt, event.endsAt, tz]);

  const dayLabel = useCallback(
    (i: number): string =>
      dayjs.utc(`${dayjs.utc(event.startsAt).tz(tz).format('YYYY-MM-DD')}T12:00:00Z`).add(i, 'day').format('ddd, MMM D'),
    [event.startsAt, tz],
  );
  const dayOptions = useMemo(
    () => Array.from({ length: dayCount }, (_, i) => ({ value: String(i), label: `${t('events:schedulePage.dayN', { defaultValue: 'Day {{n}}', n: i + 1 })} — ${dayLabel(i)}` })),
    [dayCount, dayLabel, t],
  );

  // Absolute UTC start/end for a relative slot — for the past guard + a duration
  // preview (so the user sees the whole thing looks right, incl. overnight).
  const relDateStr = useMemo(
    () =>
      dayjs
        .utc(`${dayjs.utc(event.startsAt).tz(tz).format('YYYY-MM-DD')}T12:00:00Z`)
        .add(form.dayOffset, 'day')
        .format('YYYY-MM-DD'),
    [event.startsAt, tz, form.dayOffset],
  );
  const relativeStartAbs = useMemo(
    () => (relative && form.startTime ? dayjs.tz(`${relDateStr} ${form.startTime}`, tz) : null),
    [relative, form.startTime, relDateStr, tz],
  );
  const relativeEndAbs = useMemo(() => {
    if (!relativeStartAbs) return null;
    if (!form.endTime) return relativeStartAbs.add(1, 'hour');
    let end = dayjs.tz(`${relDateStr} ${form.endTime}`, tz);
    if (!end.isAfter(relativeStartAbs)) end = end.add(1, 'day'); // overnight
    return end;
  }, [relativeStartAbs, form.endTime, relDateStr, tz]);

  // Absolute start/end (ISO) for whichever mode, for the duration badge.
  const previewStartIso = (relative ? relativeStartAbs : form.startsAt ? dayjs.tz(form.startsAt, tz) : null)?.toISOString() ?? null;
  const previewEndIso = (relative ? relativeEndAbs : form.endsAt ? dayjs.tz(form.endsAt, tz) : null)?.toISOString() ?? null;

  // A2e #94 — this date's own deadline cannot fall after the date ends: mark the date's day(s) and name them, on the
  // event's calendar, as the pickers are. ⓘ #137 review — a SESSION with no end time is stored ending an hour after it
  // starts (`slotMaterialize`, the same +1 hour as `relativeEndAbs`), so that is its end here too.
  const slotDays = useMemo(() => {
    if (relative) {
      return form.startTime && relativeEndAbs ? { from: relDateStr, to: relativeEndAbs.format('YYYY-MM-DD') } : null;
    }

    return form.startsAt ? { from: form.startsAt.slice(0, 10), to: (form.endsAt || form.startsAt).slice(0, 10) } : null;
  }, [relative, form.startTime, relDateStr, relativeEndAbs, form.startsAt, form.endsAt]);
  // A2e #100 — on a TICKETED event this date's deadline closes its ticket sales (the checkout refuses after it), and the
  // "RSVP to this specifically" switch means nothing there (a ticketed event takes no RSVPs) — so the field is offered
  // without it, named for tickets.
  const ticketed = event.eventMode === 'ticketed';
  const showDeadline = form.rsvpEnabled || ticketed;
  // A2e #102 — on a REPEATING event "the event's" deadline is a rule, "N days before each date", so say what it makes of
  // THIS date (calendar days on the event's clock, as the server counts them; display only — the server decides).
  const seriesDaysBefore = event.scheduleMode === 'recurring' ? event.rsvpDeadlineDaysBefore ?? null : undefined;
  const inheritedNote =
    seriesDaysBefore === undefined
      ? t('events:schedulePage.rsvpDeadlineInherit', { defaultValue: 'Leave empty to use the event’s.' })
      : seriesDaysBefore === null
        ? t('events:schedulePage.rsvpDeadlineInheritNone', { defaultValue: 'Leave empty for no deadline — the event sets none.' })
        : slotDays
          ? t('events:schedulePage.rsvpDeadlineInheritDay', {
              defaultValue: 'Leave empty to use the event’s: open through {{day}}.',
              // The end of that day (#137: a day-shaped deadline closes at its last second).
              day: dayjs(slotDays.from.slice(0, 10)).subtract(seriesDaysBefore, 'day').format('MMM D, YYYY'),
            })
          : t('events:schedulePage.rsvpDeadlineInherit', { defaultValue: 'Leave empty to use the event’s.' });
  // #137 — this date's own deadline is a date AND time, capped at the date's END to the minute (a session's end as the
  // server stores it — above; an absolute date with no end: its start; an all-day date's last day). The server refuses
  // anything later (`latestDeadlineFor`).
  const slotDeadlineMax = !slotDays
    ? null
    : relative
      ? relativeEndAbs?.format('YYYY-MM-DD HH:mm:ss') ?? null
      : deadlineCeiling({ startsAt: form.startsAt, endsAt: form.endsAt, isAllDay: form.isAllDay });
  const slotEnds = relative || !!form.endsAt;
  // #137 review — a deadline sitting at the date's end moves with it when its times change.
  const followDeadline = useCallback((next: string) => setForm((f) => ({ ...f, rsvpDeadline: next })), []);
  useDeadlineFollowsEnd(slotDeadlineMax, form.rsvpDeadline, followDeadline);
  const slotWhen = !slotDays
    ? ''
    : !relative && form.isAllDay
      ? t('events:schedulePage.rsvpDeadlineDateIs', { defaultValue: 'This date is on {{days}}.', days: formatMarkedDays(slotDays) })
      : slotEnds
        ? t('events:schedulePage.rsvpDeadlineDateEnds', { defaultValue: 'This date ends {{when}}.', when: formatPickerMoment(slotDeadlineMax) })
        : t('events:schedulePage.rsvpDeadlineDateStarts', { defaultValue: 'This date starts {{when}}.', when: formatPickerMoment(slotDeadlineMax) });
  const deadlineHint = slotWhen ? `${inheritedNote} ${slotWhen}` : inheritedNote;

  // Esc / a click outside / Cancel ask before discarding a changed slot (#123). The computed start of a NEW session
  // (after the last one) is part of the snapshot — the host did not type it.
  const { isDirty, reseed, clear } = useFormSnapshot(form);

  useEffect(() => {
    if (!opened) {
      clear();

      return;
    }

    const base = emptyForm();
    let seeded: SlotForm;
    if (relative) {
      const evStart = dayjs.utc(event.startsAt).tz(tz);
      if (slot?.dayOffset != null) {
        seeded = { ...base, ...common(slot, tz), dayOffset: slot.dayOffset, startTime: slot.startTime || '', endTime: slot.endTime || '' };
      } else if (slot?.startsAt) {
        // Legacy absolute agenda slot → convert to relative.
        const ss = dayjs.utc(slot.startsAt).tz(tz);
        seeded = {
          ...base,
          ...common(slot, tz),
          dayOffset: Math.max(0, ss.startOf('day').diff(evStart.startOf('day'), 'day')),
          startTime: ss.format('HH:mm'),
          endTime: slot.endsAt ? dayjs.utc(slot.endsAt).tz(tz).format('HH:mm') : '',
        };
      } else {
        // New relative slot — right after the LAST session (its end, and its length), else at the event's start.
        // ⚠ Sessions follow SESSIONS: dates left from a repeat pattern (weeks ahead) are not "the previous one".
        const sessions = siblings.filter((s) => !s.isGenerated);
        const last = latestEnd(sessions, (s) => s.endsAt);
        const lastSlot = last ? sessions.find((s) => s.endsAt === last) : undefined;
        const from = last ? dayjs.utc(last).tz(tz) : evStart;
        const lengthMin = lastSlot?.startsAt ? Math.max(5, dayjs.utc(last).diff(dayjs.utc(lastSlot.startsAt), 'minute')) : 60;

        seeded = {
          ...base,
          dayOffset: Math.max(0, from.startOf('day').diff(evStart.startOf('day'), 'day')),
          startTime: from.format('HH:mm'),
          endTime: from.add(lengthMin, 'minute').format('HH:mm'),
        };
      }
    } else if (slot) {
      seeded = { ...base, ...common(slot, tz), startsAt: toPicker(slot.startsAt, tz, !!slot.isAllDay), endsAt: toPicker(slot.endsAt, tz, !!slot.isAllDay), isAllDay: !!slot.isAllDay };
    } else {
      seeded = base;
    }
    // A refreshed session keeps what the user has typed (#125); another session replaces the form.
    setForm(reseed(seeded, slot?.id ?? '__new__'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, slot]);

  // Autofocus the name field once the open animation + focus-trap settle, caret
  // at the end (matches the em-scaffold-ui drawer pattern).
  useEffect(() => {
    if (!opened) return undefined;
    const id = setTimeout(() => {
      const el = nameInputRef.current;
      if (el) {
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
    }, 120);
    return () => clearTimeout(id);
  }, [opened]);

  // Absolute-path handler (recurring custom): keep-duration, end ≥ start.
  const handleAbsChange = (name: string | undefined, value: unknown) => {
    if (!name) return;
    setForm((f) => {
      if (name === 'startsAt') {
        const v = (value as string | null) ?? null;
        if (!v) return { ...f, startsAt: null };
        if (f.startsAt && f.endsAt) return { ...f, startsAt: v, endsAt: moveEndWithStart(f.startsAt, f.endsAt, v) };
        if (!f.endsAt && !f.isAllDay) return { ...f, startsAt: v, endsAt: addMinutesToDateTime(v, 60) };
        return { ...f, startsAt: v };
      }
      if (name === 'endsAt') {
        const v = (value as string | null) ?? null;
        return { ...f, endsAt: v && isEndBeforeStart(f.startsAt, v) ? f.startsAt : v };
      }
      if (name === 'isAllDay') {
        const checked = !!value;
        return { ...f, isAllDay: checked, startsAt: normalizePickerValue(f.startsAt, checked), endsAt: normalizePickerValue(f.endsAt, checked) };
      }
      return { ...f, [name]: value };
    });
  };
  const handleField = (name: string | undefined, value: unknown) => {
    if (name) setForm((f) => ({ ...f, [name]: value }));
  };

  const handleVenueChange = (venue: Venue | null) => setForm((f) => ({ ...f, venueId: venue?.id ?? null }));

  const openMeetingUrl = () => {
    const url = form.onlineMeetingUrl.trim();
    if (!url) return;
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  // Relative time handlers: start keeps the duration (or +1h); end may be overnight.
  const onStartTime = (val: string) =>
    setForm((f) => {
      if (!val) return { ...f, startTime: '' };
      if (f.startTime && f.endTime) return { ...f, startTime: val, endTime: shiftEndTimeKeepingDuration(f.startTime, f.endTime, val) };
      if (!f.endTime) return { ...f, startTime: val, endTime: minToTime(timeToMin(val) + 60) };
      return { ...f, startTime: val };
    });

  const overnight = relative && !!form.endTime && !!form.startTime && timeToMin(form.endTime) <= timeToMin(form.startTime);

  // Save gating.
  const startPastAbs = !relative && isPastDateTime(form.startsAt, form.isAllDay, tz);
  const startPastRel = relative && !!relativeStartAbs && relativeStartAbs.isBefore(dayjs());
  const canSave = relative ? !!form.startTime && !startPastRel : !!form.startsAt && !startPastAbs;

  // The start/end controls differ per mode: relative renders the two TimeInputs,
  // absolute renders the two DateTimeFields. Show each server message on the
  // control that is actually on screen — in absolute mode the `startTime` /
  // `endTime` messages fold into the matching date field so they stay visible.
  const startTimeError = relative
    ? fieldErrors.startTime ||
      (startPastRel ? t('events:schedulePage.startPast', { defaultValue: 'The start can’t be in the past.' }) : undefined)
    : undefined;
  const endTimeError = relative ? fieldErrors.endTime : undefined;

  const startsAtError = [fieldErrors.startsAt, relative ? undefined : fieldErrors.startTime].filter(Boolean).join(' ') || undefined;
  const endsAtError = [fieldErrors.endsAt, relative ? undefined : fieldErrors.endTime].filter(Boolean).join(' ') || undefined;

  const save = async () => {
    if (!canSave) {
      toast.error(t('events:schedulePage.startPast', { defaultValue: 'The start can’t be in the past.' }));
      return;
    }
    setSaving(true);
    setSubmitError(null);
    // #199 — a date is CANCELLED by its own action (`onCancelDate`), never by this save. Here only a cancelled date's
    // reason is edited, or the date REOPENED (the switch off → canceledAt null). A live date sends neither.
    const cancelFields = slot?.canceledAt
      ? {
          canceledAt: form.canceled ? slot.canceledAt : null,
          cancelReason: form.canceled ? form.cancelReason.trim() || null : null,
        }
      : {};
    const shared = {
      name: form.name.trim() || null,
      description: showAgenda ? form.description.trim() || null : undefined,
      descriptionFormat: showAgenda ? form.descriptionFormat : undefined,
      speakerGuestIds: form.speakerGuestIds,
      track: showAgenda ? form.track.trim() || null : undefined,
      rsvpEnabled: form.rsvpEnabled,
      capacity: form.capacity,
      venueId: form.venueId,
      mapElementId: form.mapElementId,
      // ⓘ No acts: they are booked and timed on the Artists page (#165 round 2). Not sending them leaves them as they are —
      //   a date drawer opened before a change there must not write its stale list back.
      onlineMeetingUrl: form.onlineMeetingUrl.trim() || null,
      // The deadline only matters when this sub-event takes its own RSVPs — or sells its own tickets (#100).
      rsvpDeadline: showDeadline ? zonedStringToUtc(form.rsvpDeadline, tz) : null,
      // #41 — '' means "inherit the event's", which is null on the column. Number('') is 0, and 0
      // is a real answer here ("doors open exactly at the start"), so the empty case is explicit.
      doorsOpenMinutes: form.doorsOpenMinutes === '' ? null : Number(form.doorsOpenMinutes),
      ...cancelFields,
    };
    const input: EventTimeSlotInput = relative
      ? { ...shared, dayOffset: form.dayOffset, startTime: form.startTime, endTime: form.endTime || null }
      : { ...shared, startsAt: zonedStringToUtc(form.startsAt, tz), endsAt: zonedStringToUtc(form.endsAt, tz), isAllDay: form.isAllDay };

    const result = slot?.id
      ? await EventTimeSlotService.update(slot.id, input)
      : await EventTimeSlotService.create({ ...input, eventId });
    setSaving(false);
    if (result.didSucceed()) {
      // No success toast (house rule): its highlighted row on the schedule is the confirmation.
      onSaved(slot?.id ?? (result.getDataFirst() as EventTimeSlot | undefined)?.id ?? null);
      onClose();
    } else {
      // Keep the OpResult: field errors go under their inputs, the rest above Save.
      setSubmitError(result);

      return;
    }
  };

  // ⌘/Ctrl+S or ⌘/Ctrl+Enter saves (works from any focused field); Esc is AppDrawer's own.
  // Off while the description is in full screen, so ⌘S there belongs to the full-screen editor.
  useModalShortcuts({
    onConfirm: save,
    canConfirm: canSave && !saving,
    enabled: opened && !descriptionFullscreen,
  });

  const title = slot
    ? t('events:schedulePage.editSlot', { defaultValue: 'Edit slot' })
    : showAgenda
      ? t('events:schedulePage.addSubEvent', { defaultValue: 'Add sub-event' })
      : t('events:schedulePage.addSlot', { defaultValue: 'Add a date' });

  return (
    <VenuesProvider pageSize={WHOLE_SET} initialFilters={{ kind: [VENUE_KIND.VENUE] }}>
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={title}
      size="md"
      closable={!descriptionFullscreen}
      loading={saving}
      errors={formatErrors(genericErrors)}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={save}
      confirmLabel={t('common:buttons.save', { defaultValue: 'Save' })}
      cancelLabel={t('common:buttons.cancel', { defaultValue: 'Cancel' })}
      confirmDisabled={!canSave}
      confirmShortcut={SHORTCUT_HINTS.save}
      isDirty={isDirty}
    >
      <Stack gap="md">
        <TextField
          ref={nameInputRef}
          name="name"
          label={t('events:schedulePage.slotName', { defaultValue: 'Name' })}
          placeholder={
            showAgenda
              ? t('events:schedulePage.subEventNamePh', { defaultValue: 'e.g. Ceremony, Reception' })
              : t('events:schedulePage.slotNamePh', { defaultValue: 'Optional — labelled by date otherwise' })
          }
          value={form.name}
          onChange={handleField}
          error={fieldErrors.name}
        />

        {relative ? (
          <>
            {dayCount > 1 && (
              <SelectField
                name="dayOffset"
                label={t('events:schedulePage.day', { defaultValue: 'Day' })}
                data={dayOptions}
                value={String(form.dayOffset)}
                onChange={(_n, v) => handleField('dayOffset', Number(v) || 0)}
                allowDeselect={false}
                error={fieldErrors.dayOffset}
              />
            )}
            <Group gap="sm" grow align="flex-start">
              <TimeInput
                label={t('events:schedulePage.startTime', { defaultValue: 'Start time' })}
                value={form.startTime}
                onChange={(e) => onStartTime(e.currentTarget.value)}
                error={startTimeError}
              />
              <TimeInput
                label={t('events:schedulePage.endTime', { defaultValue: 'End time' })}
                value={form.endTime}
                onChange={(e) => handleField('endTime', e.currentTarget.value)}
                error={endTimeError}
              />
            </Group>
            {overnight && (
              <Text c="dimmed">
                {t('events:schedulePage.overnightNote', { defaultValue: 'Ends the next day (overnight).' })}
              </Text>
            )}
          </>
        ) : (
          <>
            <SwitchField
              name="isAllDay"
              label={t('events:schedulePage.allDay', { defaultValue: 'All day' })}
              checked={form.isAllDay}
              onChange={handleAbsChange}
              error={fieldErrors.isAllDay}
            />
            <DateTimeField
              name="startsAt"
              label={t('events:schedulePage.starts', { defaultValue: 'Starts' })}
              value={form.startsAt}
              onChange={handleAbsChange}
              dateOnly={form.isAllDay}
              disallowPast
              timeZone={tz}
              pastErrorText={t('events:schedulePage.startPast', { defaultValue: 'The start can’t be in the past.' })}
              clearable
              error={startsAtError}
            />
            <DateTimeField
              name="endsAt"
              label={t('events:schedulePage.ends', { defaultValue: 'Ends' })}
              value={form.endsAt}
              onChange={handleAbsChange}
              dateOnly={form.isAllDay}
              notBefore={form.startsAt}
              clearable
              error={endsAtError}
            />
            {zoneNote && (
              <Text size="md" c="dimmed">
                {zoneNote}
              </Text>
            )}
          </>
        )}

        <EventDurationBadge startsAt={previewStartIso} endsAt={previewEndIso} tz={tz} style={{ alignSelf: 'flex-start' }} />

        {showAgenda && (
          <>
            {/* Rich text, Markdown, HTML or plain, with a full-screen mode — the editor policies and the event's own
                description use; the public schedule renders it through the matching sanitizing renderer. */}
            <PolicyTextEditor
              label={t('events:schedulePage.slotDescription', { defaultValue: 'Description' })}
              format={form.descriptionFormat}
              onFormatChange={(descriptionFormat) =>
                setForm((f) => ({ ...f, descriptionFormat, description: carryDescriptionOver(f.description, f.descriptionFormat, descriptionFormat) }))
              }
              value={form.description}
              onChange={(description) => setForm((f) => ({ ...f, description }))}
              variables={NO_VARIABLES}
              onFullscreenChange={setDescriptionFullscreen}
              error={[fieldErrors.description, fieldErrors.descriptionFormat].filter(Boolean).join(' ') || undefined}
            />
            <TextField
              name="track"
              label={t('events:schedulePage.track', { defaultValue: 'Track / room' })}
              description={t('events:schedulePage.trackHint', { defaultValue: 'For parallel sessions, e.g. “Main Hall”' })}
              value={form.track}
              onChange={handleField}
              error={fieldErrors.track}
            />
          </>
        )}

        <SlotSpeakersField
          eventId={eventId}
          value={form.speakerGuestIds}
          onChange={(speakerGuestIds) => setForm((f) => ({ ...f, speakerGuestIds }))}
          disabled={saving}
          error={fieldErrors.speakerGuestIds}
        />

        <Divider label={t('events:schedulePage.whereSection', { defaultValue: 'Where' })} labelPosition="left" />

        {/* Per-slot venue (blank = use the event's) + in-place "add venue". */}
        <VenuePickerField value={form.venueId} onChange={handleVenueChange} error={fieldErrors.venueId} />

        {/* On-site map element this session happens at (pairs with the track label). */}
        <MapElementSelect
          eventId={eventId}
          value={form.mapElementId}
          onChange={(mapElementId) => handleField('mapElementId', mapElementId)}
          description={t('events:schedulePage.mapElementHint', {
            defaultValue: 'Link to a stage/booth on the event map',
          })}
          error={fieldErrors.mapElementId}
        />

        {/* The acts playing this date / session (#70), READ-ONLY here — in playing order with their sets; they are booked and
            timed on the Artists page (#165 round 2: the same act on another date may have other terms, a booking each). */}
        <SlotActsSummary
          eventId={eventId}
          slotId={slot?.id ?? null}
          slotStartsAt={slot?.startsAt ?? null}
          timezone={event.timezone ?? null}
          isRecurring={!showAgenda}
        />

        <TextField
          name="onlineMeetingUrl"
          label={t('events:schedulePage.meetingUrl', { defaultValue: 'Online meeting link' })}
          description={t('events:schedulePage.meetingUrlHint', { defaultValue: 'For a virtual or hybrid session' })}
          placeholder="https://…"
          value={form.onlineMeetingUrl}
          onChange={handleField}
          type="url"
          rightSectionPointerEvents={form.onlineMeetingUrl.trim() ? 'auto' : 'none'}
          rightSection={
            form.onlineMeetingUrl.trim() ? (
              <Tooltip label={t('events:schedulePage.openLink', { defaultValue: 'Open link' })} withArrow>
                <ActionIcon
                  variant="subtle"
                  color="grape"
                  onClick={openMeetingUrl}
                  aria-label={t('events:schedulePage.openLink', { defaultValue: 'Open link' })}
                >
                  <AppIcon iconName="ExternalLink" size={16} />
                </ActionIcon>
              </Tooltip>
            ) : undefined
          }
          error={fieldErrors.onlineMeetingUrl}
        />

        <Divider
          label={ticketed ? t('events:schedulePage.ticketsSection', { defaultValue: 'Tickets' }) : t('events:schedulePage.rsvpSection', { defaultValue: 'RSVP' })}
          labelPosition="left"
        />

        {!ticketed && (
          <SwitchField
            name="rsvpEnabled"
            label={t('events:schedulePage.rsvpEnabled', { defaultValue: 'Guests can RSVP to this specifically' })}
            checked={form.rsvpEnabled}
            onChange={handleField}
            error={fieldErrors.rsvpEnabled}
          />
        )}

        {showDeadline && (
          <DateTimeField
            name="rsvpDeadline"
            label={
              ticketed
                ? t('events:schedulePage.ticketDeadline', { defaultValue: 'Ticket sales for this date end' })
                : t('events:schedulePage.rsvpDeadline', { defaultValue: 'RSVPs close' })
            }
            description={deadlineHint}
            value={form.rsvpDeadline}
            onChange={handleField}
            notAfter={slotDeadlineMax}
            notAfterErrorText={
              slotEnds
                ? t('events:schedulePage.deadlineAfterDate', { defaultValue: 'Can’t be after this date ends' })
                : t('events:schedulePage.deadlineAfterDateStart', { defaultValue: 'Can’t be after this date starts' })
            }
            defaultTime={DEADLINE_END_OF_DAY}
            markDays={slotDays}
            clearable
            error={fieldErrors.rsvpDeadline}
          />
        )}

        {/* #41 — this date's own door. Empty inherits the event's, which itself falls back to 90. */}
        <SelectField
          name="doorsOpenMinutes"
          label={t('events:schedulePage.slotDoorsOpen', { defaultValue: 'Doors open' })}
          description={t('events:schedulePage.slotDoorsOpenHint', {
            defaultValue: 'When crew can start checking guests in to this date. Leave as the event’s unless this one differs. Guests see “Doors open at …” only when a time is chosen here or for the event — not for the default.',
          })}
          data={doorsOpenOptions(
            form.doorsOpenMinutes === '' ? null : Number(form.doorsOpenMinutes),
            t('events:schedulePage.slotDoorsOpenInherit', {
              defaultValue: `Same as the event (${formatDoorsOpenLead(resolveDoorsOpenMinutes(null, event))})`,
            }),
          )}
          value={form.doorsOpenMinutes}
          onChange={handleField}
          allowDeselect={false}
          error={fieldErrors.doorsOpenMinutes}
        />

        <NumberField
          name="capacity"
          label={t('events:schedulePage.slotCapacity', { defaultValue: 'Capacity' })}
          description={t('events:schedulePage.slotCapacityHint', { defaultValue: 'Leave empty to use the event’s' })}
          placeholder={t('events:schedulePage.slotCapacityPh', { defaultValue: 'Event default' })}
          value={form.capacity}
          onChange={handleField}
          min={1}
          error={fieldErrors.capacity}
        />

        {/* #199 — a CANCELLED date: its reason, and Reopen (the switch off). A live one: the way to cancel it, which is
            its own drawer (it tells everyone booked and can refund them), never a field saved with this form. */}
        {slot?.canceledAt ? (
          <>
            <Divider label={t('events:schedulePage.statusSection', { defaultValue: 'Status' })} labelPosition="left" />
            <SwitchField
              name="canceled"
              label={t('events:schedulePage.canceledSwitch', { defaultValue: 'Cancelled' })}
              description={
                form.canceled
                  ? t('events:schedulePage.canceledHintOn', { defaultValue: 'Turn off and save to reopen this date.' })
                  : t('events:schedulePage.canceledHintReopen', {
                      defaultValue:
                        'Saving reopens this date: it takes tickets and RSVPs again and the tickets nobody refunded are valid again. Refunds already sent stay sent; a date whose refunds are still going out can be reopened once they have. Nobody is emailed.',
                    })
              }
              checked={form.canceled}
              onChange={handleField}
              error={fieldErrors.canceled}
            />
            {form.canceled && (
              <TextAreaField
                name="cancelReason"
                label={t('events:schedulePage.cancelReason', { defaultValue: 'Reason' })}
                description={t('events:schedulePage.cancelReasonHint', { defaultValue: 'Shown on the tickets for this date. Already-sent emails keep the reason they had.' })}
                value={form.cancelReason}
                onChange={handleField}
                autosize
                minRows={2}
                maxRows={4}
                error={fieldErrors.cancelReason}
              />
            )}
            {onCancelDate && (
              <DiscardGuard>
                {(leave) => (
                  <Group>
                    <Button variant="light" size="md" leftSection={<AppIcon iconName="CalendarMinus" size={16} />} onClick={() => leave(() => onCancelDate(slot))}>
                      {t('events:schedulePage.cancelDateView', { defaultValue: 'Cancellation and refunds' })}
                    </Button>
                  </Group>
                )}
              </DiscardGuard>
            )}
          </>
        ) : slot && onCancelDate && canCancelDate(slot, event) ? (
          <>
            <Divider label={t('events:schedulePage.statusSection', { defaultValue: 'Status' })} labelPosition="left" />
            <Text size="md" c="dimmed">
              {showAgenda
                ? t('events:schedulePage.cancelSessionIntro', {
                    defaultValue: 'Calling this session off? Everyone booked on it is emailed. The rest of the event is not affected.',
                  })
                : t('events:schedulePage.cancelDateIntro', {
                    defaultValue: 'Calling this date off? Everyone booked on it is emailed, and you can refund them. The other dates are not affected.',
                  })}
            </Text>
            <DiscardGuard>
              {(leave) => (
                <Group>
                  <Button color="red" variant="light" size="md" leftSection={<AppIcon iconName="CalendarMinus" size={16} />} onClick={() => leave(() => onCancelDate(slot))}>
                    {showAgenda
                      ? t('events:schedulePage.cancelSessionButton', { defaultValue: 'Cancel this session…' })
                      : t('events:schedulePage.cancelDateButton', { defaultValue: 'Cancel this date…' })}
                  </Button>
                </Group>
              )}
            </DiscardGuard>
          </>
        ) : null}
      </Stack>
    </AppDrawer>
    </VenuesProvider>
  );
};

// Fields common to both modes when editing an existing slot.
const common = (s: EventTimeSlot, tz: string) => ({
  name: s.name ?? '',
  description: s.description ?? '',
  // Rows written before migration 1970 are plain text (that is what the column defaults them to).
  descriptionFormat: s.descriptionFormat || PLAIN,
  speakerGuestIds: (s.speakerGuestIds ?? []).filter((id): id is string => !!id),
  track: s.track ?? '',
  rsvpEnabled: !!s.rsvpEnabled,
  capacity: s.capacity ?? null,
  venueId: s.venueId ?? null,
  mapElementId: s.mapElementId ?? null,
  onlineMeetingUrl: s.onlineMeetingUrl ?? '',
  rsvpDeadline: toPicker(s.rsvpDeadline, tz),
  doorsOpenMinutes: s.doorsOpenMinutes === null || s.doorsOpenMinutes === undefined ? '' : String(s.doorsOpenMinutes),
  canceled: !!s.canceledAt,
  cancelReason: s.cancelReason ?? '',
});
